import {
  ArrowRight,
  CheckCircle2,
  Eye,
  FileText,
  ListTodo,
  StickyNote,
  UserCheck,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useContractingTimeline } from "@/hooks/queries/useContractingTimeline";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import type { TimelineEntry } from "@/types/domain";

const STATUS_LABELS: Record<number, string> = {
  0: "sin estado",
  1: "revisada",
  2: "negociando",
  3: "vendida",
  4: "perdida",
};

const ICONS = {
  STATUS_CHANGED: FileText,
  ASSIGNED: UserCheck,
  NOTE_UPDATED: StickyNote,
  NOTE_ADDED: StickyNote,
  TASK_CREATED: ListTodo,
  TASK_COMPLETED: CheckCircle2,
  UNLOCKED: Eye,
} as const;

/** Qué pasó, en una frase. El sujeto (quién) se muestra aparte. */
function describe(entry: TimelineEntry) {
  switch (entry.type) {
    case "STATUS_CHANGED":
      if (entry.toStatus === null) return "quitó el estado";
      return (
        <>
          movió a{" "}
          <strong className="font-semibold text-slate-700 dark:text-slate-200">
            {STATUS_LABELS[entry.toStatus] ?? "otro estado"}
          </strong>
          {entry.fromStatus !== null && (
            <span className="text-slate-400">
              {" "}
              <ArrowRight className="inline size-3" aria-hidden /> desde{" "}
              {STATUS_LABELS[entry.fromStatus] ?? "otro estado"}
            </span>
          )}
        </>
      );
    case "ASSIGNED":
      return entry.target ? (
        <>
          se la traspasó a{" "}
          <strong className="font-semibold text-slate-700 dark:text-slate-200">
            {entry.target.name}
          </strong>
        </>
      ) : (
        "quitó al responsable"
      );
    case "NOTE_UPDATED":
      return "editó la nota";
    case "NOTE_ADDED":
      return "comentó";
    case "TASK_CREATED":
      return entry.target ? (
        <>
          dejó una tarea a{" "}
          <strong className="font-semibold text-slate-700 dark:text-slate-200">
            {entry.target.name}
          </strong>
        </>
      ) : (
        "creó una tarea"
      );
    case "TASK_COMPLETED":
      return "completó una tarea";
    case "UNLOCKED":
      return "abrió la oportunidad";
  }
}

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString("es-PE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Historial de la licitación: quién hizo qué y a quién se la traspasó. Lo ve todo
 * el equipo, porque el punto es justamente que no se pisen.
 */
export function ContractingTimeline({
  contractingId,
}: {
  contractingId: number;
}) {
  const { data: entries, isLoading } = useContractingTimeline(contractingId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!entries?.length) {
    return (
      <p className="text-sm text-slate-400 dark:text-slate-500">
        Todavía no hay movimientos en esta oportunidad.
      </p>
    );
  }

  return (
    <ol className="flex flex-col">
      {entries.map((entry, index) => {
        const Icon = ICONS[entry.type];
        const isLast = index === entries.length - 1;
        return (
          <li key={entry.id} className="flex gap-3">
            {/* Riel: el icono con la línea que conecta con el evento siguiente. */}
            <div className="flex flex-col items-center">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <Icon className="size-3.5" />
              </span>
              {!isLast && (
                <span className="w-px flex-1 bg-slate-200 dark:bg-slate-700" />
              )}
            </div>

            <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-1 pb-4 text-sm">
              {entry.actor && (
                <MemberAvatar member={entry.actor} className="size-5" />
              )}
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {entry.actor?.name ?? "Alguien"}
              </span>
              <span className="text-slate-500 dark:text-slate-400">
                {describe(entry)}
              </span>
              <time
                dateTime={entry.at}
                className="text-xs text-slate-400 dark:text-slate-500"
              >
                {formatWhen(entry.at)}
              </time>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
